import { getPublicOrigin, getPublicPath } from './public-routes.js'

const PUBLIC_META = {
    en: {
        Tapgiant: {
            title: 'Cake Shop Order Management Software | Tapgiant',
            description: 'Keep phone, WhatsApp, social media, and in-store cake orders in one place. Track customer details, pickup dates, advance payments, and order status.'
        },
        how_it_works: {
            title: 'How Tapgiant Works for Cake Shops',
            description: 'See how a cake shop records customer orders, follows preparation, updates status, and keeps the complete order history with Tapgiant.'
        },
        store_provisioning: {
            title: 'Store and Kiosk Provisioning Software | Tapgiant',
            description: 'Let every location submit its daily needs while the central team prepares, delivers, and tracks provisioning orders in one workflow.'
        },
        push_notifications: {
            title: 'Enable Tapgiant Delivery Notifications on iPhone',
            description: 'Follow five illustrated steps to add Tapgiant Delivery to your iPhone Home Screen and enable notifications for new and updated orders.'
        },
        push_notifications_android: {
            title: 'Enable Tapgiant Delivery Notifications on Android',
            description: 'Follow the illustrated Android installation paths to add Tapgiant Delivery to your phone and enable notifications for new and updated orders.'
        },
        pricing: {
            title: 'Tapgiant Pricing for Cake Shops and Multi-Location Teams',
            description: 'Compare Tapgiant plans for customer order management and daily store provisioning. Start with a 14-day trial and no card required.'
        },
        contact: {
            title: 'Contact Tapgiant',
            description: 'Ask about using Tapgiant for cake shop orders, team workflows, or provisioning multiple stores and kiosks.'
        },
        privacy: {
            title: 'Privacy Policy | Tapgiant',
            description: 'Learn how Tapgiant processes account, order, technical, billing, analytics, and Web Push data, including GDPR roles, retention, providers, and your rights.'
        },
        terms: {
            title: 'Terms and Conditions | Tapgiant',
            description: 'Read the terms for Tapgiant accounts, customer data, acceptable use, subscriptions, availability, cancellation, and legal responsibilities.'
        }
    },
    ro: {
        Tapgiant: {
            title: 'Aplicație de gestionare a comenzilor pentru cofetării | Tapgiant',
            description: 'Păstrează comenzile primite prin telefon, WhatsApp, rețele sociale sau în cofetărie într-un singur loc. Urmărește clienții, ridicarea, avansul și starea.'
        },
        how_it_works: {
            title: 'Cum Funcționează Tapgiant pentru Cofetării',
            description: 'Vezi cum înregistrezi comenzile clienților, urmărești pregătirea, actualizezi starea și păstrezi istoricul complet cu Tapgiant.'
        },
        store_provisioning: {
            title: 'Aprovizionarea Punctelor de Lucru | Tapgiant',
            description: 'Fiecare locație trimite necesarul zilnic, iar echipa centrală pregătește, livrează și urmărește comenzile într-un singur flux.'
        },
        push_notifications: {
            title: 'Activează notificările Tapgiant Delivery pe iPhone',
            description: 'Urmează cinci pași ilustrați pentru a adăuga Tapgiant Delivery pe ecranul principal al iPhone-ului și a activa notificările pentru comenzi.'
        },
        push_notifications_android: {
            title: 'Activează notificările Tapgiant Delivery pe Android',
            description: 'Urmează pașii ilustrați pentru Android, adaugă Tapgiant Delivery pe telefon și activează notificările pentru comenzile noi și actualizate.'
        },
        pricing: {
            title: 'Abonamente Tapgiant pentru Cofetării și Echipe',
            description: 'Compară abonamentele Tapgiant pentru comenzile clienților și aprovizionarea zilnică. Începe cu 14 zile gratuit, fără card.'
        },
        contact: {
            title: 'Contact Tapgiant',
            description: 'Întreabă-ne despre folosirea Tapgiant pentru comenzile cofetăriei, activitatea echipei sau aprovizionarea mai multor locații.'
        },
        privacy: {
            title: 'Politica de confidențialitate | Tapgiant',
            description: 'Află cum prelucrează Tapgiant datele despre conturi, comenzi, facturare, analiză și Web Push, inclusiv rolurile RGPD, păstrarea, furnizorii și drepturile tale.'
        },
        terms: {
            title: 'Termeni și condiții | Tapgiant',
            description: 'Citește condițiile pentru conturile Tapgiant, datele clienților, utilizarea acceptabilă, abonamente, disponibilitate, anulare și responsabilități.'
        }
    }
}

const NOINDEX_ROUTES = new Set([
    'account_login_step1',
    'account_login_step2',
    'account_order_success',
    '404'
])

function setMeta (selector, attribute, value) {
    let element = document.head.querySelector(selector)
    if (!element) {
        element = document.createElement('meta')
        const [name, content] = attribute
        element.setAttribute(name, content)
        document.head.appendChild(element)
    }
    element.setAttribute('content', value)
}

function setLink (rel, href, hreflang) {
    const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`
    let element = document.head.querySelector(selector)
    if (!element) {
        element = document.createElement('link')
        element.setAttribute('rel', rel)
        if (hreflang) element.setAttribute('hreflang', hreflang)
        document.head.appendChild(element)
    }
    element.setAttribute('href', href)
}

export function applySeo (route, locale) {
    const language = locale === 'ro' ? 'ro' : 'en'
    const meta = PUBLIC_META[language][route.name]
    const isConsole = route.path.startsWith('/console')
    const shouldIndex = Boolean(meta) && !isConsole && !NOINDEX_ROUTES.has(route.name)
    const description = meta?.description || PUBLIC_META[language].Tapgiant.description
    const title = meta?.title || (isConsole ? 'Tapgiant Console' : 'Tapgiant')
    const canonicalPath = getPublicPath(route.name, language) || (route.path === '/' ? '/' : route.path.replace(/\/$/, ''))
    const canonicalOrigin = getPublicOrigin(language)
    const englishPath = getPublicPath(route.name, 'en')
    const romanianPath = getPublicPath(route.name, 'ro')

    document.title = title
    document.documentElement.setAttribute('lang', language)
    setMeta('meta[name="description"]', ['name', 'description'], description)
    setMeta('meta[name="robots"]', ['name', 'robots'], shouldIndex ? 'index, follow' : 'noindex, nofollow')
    setMeta('meta[property="og:title"]', ['property', 'og:title'], title)
    setMeta('meta[property="og:description"]', ['property', 'og:description'], description)
    setMeta('meta[property="og:type"]', ['property', 'og:type'], 'website')
    setMeta('meta[property="og:locale"]', ['property', 'og:locale'], language === 'ro' ? 'ro_RO' : 'en_US')
    setMeta('meta[property="og:url"]', ['property', 'og:url'], `${canonicalOrigin}${canonicalPath}`)
    setMeta('meta[name="twitter:card"]', ['name', 'twitter:card'], 'summary_large_image')
    setMeta('meta[name="twitter:title"]', ['name', 'twitter:title'], title)
    setMeta('meta[name="twitter:description"]', ['name', 'twitter:description'], description)
    setLink('canonical', `${canonicalOrigin}${canonicalPath}`)

    if (shouldIndex && englishPath && romanianPath) {
        setLink('alternate', `${getPublicOrigin('en')}${englishPath}`, 'en')
        setLink('alternate', `${getPublicOrigin('ro')}${romanianPath}`, 'ro')
        setLink('alternate', `${getPublicOrigin('en')}${englishPath}`, 'x-default')
    }
}
